import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {DemurrageSlab} from '@shared/models/rate-card.model';
import {takeUntil, tap} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {RateTier} from '@shared/models/rate-tier.model';
import {CreateTierComponent} from '@shared/create-tier/create-tier.component';
import {DialogHandlerService} from '@core/services/dialog-handler.service';
import {VehicleCategory} from '@shared/models/vehicle-category.model';

@Component({
    selector: 'hmt-flag-down-rate',
    templateUrl: './flag-down-rate.component.html',
    styleUrls: ['./flag-down-rate.component.scss'],
    standalone: false
})
export class FlagDownRateComponent implements OnInit {
  @Input() flagDownRateForm: FormGroup;
  @Input() vehicleCategory: VehicleCategory;
  @Input() set setIndex(index: number){
    this.index = index;
  }
  @Input() rateTiers: RateTier[];
  @Input() isDisabledForm = false;
  @Input() isFreezerContract = false;
  @Output() removeFlagDownRate = new EventEmitter<number>();
  @Output() saveNewRateTier = new EventEmitter<RateTier>();
  @Output() vehicleTierChange = new EventEmitter<RateTier>();
  index: number;
  private unsubscribe: Subject<void> = new Subject();
  dct: string; // short name for demurrageCalculationType
  demurragePerHrActivated: boolean;
  isCheckBoxUseTier = false;
  isCheckBoxFixedPrice = false;

  icon = false;

  constructor(private rateCardFormBuilder: FormBuilder, private dialogHandlerService: DialogHandlerService) {
  }
  ngOnInit(): void {
    this.flagDownRateForm.controls.vehicleCategoryName.patchValue(this.vehicleCategory.categoryName);
    this.flagDownRateForm.controls.vehicleCategory.patchValue(this.vehicleCategory.id);
    this.demurragePerHrActivated = this.flagDownRateForm.controls.demurragePerHrActivated.value;
    this.flagDownRateForm.controls.demurragePerHrActivated.valueChanges.pipe(
      takeUntil(this.unsubscribe),
      tap((value: boolean) => {
        this.demurragePerHrActivated = value;
      }),
    ).subscribe();

    this.dct = this.flagDownRateForm.controls.demurrageCalculationType.value;
    this.flagDownRateForm.controls.demurrageCalculationType.valueChanges.pipe(
      takeUntil(this.unsubscribe),
      tap((value: string) => {
        this.dct = value;
        if (value === 'FULL_TRIP') {
          this.flagDownRateForm.controls.demurragePublicLocation.setValue(false, { emitEvent: false });
          this.flagDownRateForm.controls.demurrageOtherLocation.setValue(false, { emitEvent: false });
        }
        this.updateDemurrageFieldsState();
      }),
    ).subscribe();

    this.flagDownRateForm.controls.demurragePublicLocation.valueChanges.pipe(
      takeUntil(this.unsubscribe),
      tap(() => {
        this.updateDemurrageFieldsState();
      }),
    ).subscribe();

    this.flagDownRateForm.controls.demurrageOtherLocation.valueChanges.pipe(
      takeUntil(this.unsubscribe),
      tap(() => {
        this.updateDemurrageFieldsState();
      }),
    ).subscribe();

    this.flagDownRateForm.controls.demurragePerHrActivated.valueChanges.pipe(
      takeUntil(this.unsubscribe),
      tap(() => {
        this.updateDemurrageFieldsState();
      }),
    ).subscribe();

    // Initialize demurrage fields state
    setTimeout(() => {
      this.updateDemurrageFieldsState();
    }, 0);
  }

  click(){
    this.icon = !this.icon;
  }

  setVehicleTier(tier: RateTier) {
    this.flagDownRateForm.patchValue({
      vehicleTier: tier.tierId,
    });
    this.vehicleTierChange.emit(tier);
  }

  openCreateTier() {
    const data = {
      orgId: this.flagDownRateForm.value.orgId,
      tiers: this.rateTiers
    };
    const dialogRef = this.dialogHandlerService.openDialog(CreateTierComponent, data, {
      width: '629px',
      height: '100%',
      panelClass: 'custom-dialog-container'
    });
    dialogRef.componentInstance.savetier
      .pipe(
        takeUntil(this.unsubscribe),
        tap((newTier: RateTier) => {
          this.saveRateTier(newTier);
        })
      ).subscribe();
  }

  saveRateTier(newTier: RateTier) {
    this.saveNewRateTier.emit(newTier);
  }

  displayTierName(tierId?: string) {
    return (this.rateTiers.find((tier: RateTier) => tier.tierId === tierId)?.name) ?
      this.rateTiers.find((tier: RateTier) => tier.tierId === tierId)?.name : tierId;
  }

  toggleRadioButton(value: number) {
    if (value === 1) {
      this.isCheckBoxUseTier = true;
      this.isCheckBoxFixedPrice = false;
    } else {
      this.isCheckBoxUseTier = false;
      this.isCheckBoxFixedPrice = true;
    }
  }

  getDemurrageSlabs(): FormArray {
    const slabs = this.flagDownRateForm.get('demurrageSlabs') as FormArray;
    this.updateDemurrageFieldsState();
    return slabs;
  }

  addDemurrageSlab(): void {
    const slabs = (this.flagDownRateForm.get('demurrageSlabs') as FormArray);
    const isEnabled = this.isDemurrageFieldsEnabled();
    const newSlab = this.rateCardFormBuilder.group({
      hours: [{value: null, disabled: !isEnabled}, isEnabled ? Validators.required : null],
      rate: [{value: null, disabled: !isEnabled}, isEnabled ? Validators.required : null]
    });
    slabs.push(newSlab);
    this.updateDemurrageFieldsState();
  }

  removeDemurrageSlab(slabIndex: number): void {
    const slabs = (this.flagDownRateForm.get('demurrageSlabs') as FormArray);
    slabs.removeAt(slabIndex);
    this.updateDemurrageFieldsState();
  }

  toggleUntilClearancePerHour(slabs: DemurrageSlab[]) {
    const isEnabled = this.isDemurrageFieldsEnabled();
    const clearanceField = this.flagDownRateForm.get('demurragePerHrTillClearance');
    
    if (!slabs || slabs.length === 0) {
      clearanceField.disable();
      clearanceField.clearValidators();
    } else {
      // Only enable if demurrage is properly configured
      if (isEnabled) {
        clearanceField.enable();
        clearanceField.setValidators([Validators.required]);
      } else {
        clearanceField.disable();
        clearanceField.clearValidators();
      }
    }
    clearanceField.updateValueAndValidity({ emitEvent: false });
  }

  updateDemurrageFieldsState(): void {
    const isEnabled = this.isDemurrageFieldsEnabled();
    const demurrageSlabs = this.flagDownRateForm.get('demurrageSlabs') as FormArray;
    
    // Enable/disable all slab input fields and add/remove validators
    demurrageSlabs.controls.forEach((slab: FormGroup) => {
      if (isEnabled) {
        slab.get('hours').enable();
        slab.get('rate').enable();
        // Add required validators when demurrage is enabled
        slab.get('hours').setValidators([Validators.required]);
        slab.get('rate').setValidators([Validators.required]);
      } else {
        slab.get('hours').disable();
        slab.get('rate').disable();
        // Remove validators when demurrage is disabled
        slab.get('hours').clearValidators();
        slab.get('rate').clearValidators();
      }
      slab.get('hours').updateValueAndValidity({ emitEvent: false });
      slab.get('rate').updateValueAndValidity({ emitEvent: false });
    });

    // Update Until Clearance Per Hour field
    this.toggleUntilClearancePerHour(demurrageSlabs?.value);
  }

  isDemurrageFieldsEnabled(): boolean {
    if (!this.demurragePerHrActivated) {
      return false;
    }

    const demurrageCalculationType = this.flagDownRateForm.get('demurrageCalculationType')?.value;
    
    // If FULL_TRIP is selected, enable fields
    if (demurrageCalculationType === 'FULL_TRIP') {
      return true;
    }

    // If LOCATION_BASED is selected, check if at least one location type is selected
    if (demurrageCalculationType === 'LOCATION_BASED') {
      const publicLoc = this.flagDownRateForm.get('demurragePublicLocation')?.value;
      const otherLoc = this.flagDownRateForm.get('demurrageOtherLocation')?.value;
      return publicLoc || otherLoc;
    }

    return false;
  }

  checkLocationTypeSelected() {
    return !(this.flagDownRateForm.controls.demurragePublicLocation.value || this.flagDownRateForm.controls.demurrageOtherLocation.value);
  }

  removeRate() {
    this.removeFlagDownRate.emit(this.index);
  }

  checkDemurragePerHrActivated() {
    return this.demurragePerHrActivated;
  }
}
